import { useCallback, useMemo, useState } from 'react'
import type { Creative, CreativeType, PinterestPinContent } from '../types'
import { fieldLengthState, platformContentViolations } from '../utils/validatePlatformContent'
import { useFlushPersistNow, useUpdateCreativeContent } from '../store/draft-creative-context'
import { FieldGroup } from '../components/FieldGroup'
import { ImagePickerModal } from '../components/ImagePickerModal'
import { useEditorHighlight } from '../hooks/useEditorHighlight'
import { ImagePickerButton } from './fields/ImagePickerButton'
import { VideoPickerBody } from './fields/VideoPickerBody'

interface PinterestPinEditorProps {
  creative: Creative<CreativeType.PinterestPin>
}

export function PinterestPinEditor({ creative }: PinterestPinEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const flushPersistNow = useFlushPersistNow()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content
  const [imagePickerOpen, setImagePickerOpen] = useState(false)

  const update = useCallback(
    (field: keyof PinterestPinContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  const hasVideo = !!(content.videoAttachmentId || content.videoUrl || content.videoStatus)

  // Live preflight against Pinterest's own published PinCreate limits (title
  // 100, description 800, alt_text 500, link 2048 — Pinterest REST API v5
  // OpenAPI). Same validator and same copy the server enforces on every
  // schedule/publish path, so what the composer says here is exactly what a
  // schedule attempt would say back. Before this, the description box capped
  // typing at 500 — a number that matched neither Pinterest's real 800 nor the
  // AI-written descriptions, which bypass `maxLength` entirely because it only
  // constrains keystrokes and pastes, never a programmatic value.
  const violationByField = useMemo(() => {
    const map = new Map<string, string>()
    for (const v of platformContentViolations({ type: creative.type, content })) {
      if (!map.has(v.contentField)) map.set(v.contentField, v.message)
    }
    return map
  }, [creative.type, content])

  const titleLength = fieldLengthState(creative.type, 'title', content)
  const descriptionLength = fieldLengthState(creative.type, 'description', content)
  const altTextLength = fieldLengthState(creative.type, 'altText', content)

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      {hasVideo ? (
        <FieldGroup label="Video" sectionId="video" highlighted={highlightedSection === 'video'}>
          <VideoPickerBody
            creativeId={creative.id}
            creativeType={creative.type}
            platform="pinterest"
            selectedVideoAttachmentId={content.videoAttachmentId}
            selectedVideoUrl={content.videoUrl}
            onSelectVideo={(attachmentId, videoUrl, videoSource) => {
              updateContent(creative.id, { videoAttachmentId: attachmentId, videoUrl, videoSource })
              flushPersistNow(creative.id)
            }}
          />
        </FieldGroup>
      ) : (
        <FieldGroup label="Image" sectionId="image" highlighted={highlightedSection === 'image'}>
          <ImagePickerButton
            imageUrl={content.imageUrl}
            onClick={() => setImagePickerOpen(true)}
            emptyStateHint="Recommended: 1000x1500 (2:3 ratio)"
          />
        </FieldGroup>
      )}

      <FieldGroup
        label="Title"
        count={titleLength ? `${titleLength.count}/${titleLength.max}` : undefined}
        countOver={titleLength?.over}
        error={violationByField.get('title')}
        sectionId="title"
        highlighted={highlightedSection === 'title'}
      >
        <input
          type="text"
          value={content.title}
          onChange={e => update('title', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'title')}
          placeholder="Add a title..."
          className="w-full rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label="Description"
        count={
          descriptionLength ? `${descriptionLength.count}/${descriptionLength.max}` : undefined
        }
        countOver={descriptionLength?.over}
        error={violationByField.get('description')}
        sectionId="description"
        highlighted={highlightedSection === 'description'}
      >
        <textarea
          value={content.description}
          onChange={e => update('description', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'description')}
          placeholder="Tell everyone what your pin is about..."
          rows={4}
          className="w-full resize-none rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label="Link URL"
        error={violationByField.get('link')}
        sectionId="link"
        highlighted={highlightedSection === 'link'}
      >
        <input
          type="url"
          value={content.link ?? ''}
          onChange={e => update('link', e.target.value || null)}
          onFocusCapture={() => setActiveSection(creative.id, 'link')}
          placeholder="https://yourwebsite.com (optional)"
          className="w-full rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label="Alt Text"
        count={altTextLength ? `${altTextLength.count}/${altTextLength.max}` : undefined}
        countOver={altTextLength?.over}
        error={violationByField.get('altText')}
        sectionId="altText"
        highlighted={highlightedSection === 'altText'}
      >
        <input
          type="text"
          value={content.altText}
          onChange={e => update('altText', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'altText')}
          placeholder="Describe your image for accessibility..."
          className="w-full rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <ImagePickerModal
        creativeId={creative.id}
        currentImageUrl={content.imageUrl}
        currentAttachmentId={content.imageAttachmentId}
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={({ imageUrl, imageAttachmentId }) => {
          updateContent(creative.id, { imageUrl, imageAttachmentId })
          setImagePickerOpen(false)
        }}
      />
    </div>
  )
}
